/**
 * Design estimate panel for the infinite board (eval/TAKEOFF_PLAN.md "Design
 * estimate"): fast fixture library + quick-add bar, project fixture types
 * (1–9 favourites), the selected fixture / frame, the live ROM budget, the BOM
 * and exports. Canvas operations (arming the fixture tool, placing, editing
 * a placed fixture) are the board app's — passed in as callbacks.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { dispatchNativeKey } from '../keymap';
import { FIXTURE_PRESETS, PRESET_GROUPS, itemFromPreset, presetUom, money, moneyRange, parseQuickAdd, type AlongRun, type FixturePreset } from '../estimate';
import { asTakeoffSession, type BoardEstimate } from '../hooks/useBoardEstimate';
import type { TakeoffTarget } from '../hooks/useTakeoff';
import { nextTypeCode, type PricingSettings, type TakeoffItemDTO } from '../takeoff';
import { ToastHost, toast } from './basics';
import { WorkbookButton, WorkbookPricing, useWorkbookLink } from './Workbook';
import { ImageScaleCard, MEASURE_DISPLAY_OPTIONS, MeasureLine, type SelectedImage, type SelectedMeasure } from './EstimateMeasure';
import { AssemblyEditor, ItemEditor, SymbolSwatch, TotalsPanel } from './Takeoff';
import { ScopeSelect } from './ScopeSelect';
import { scopeChoices, type ScopeChoices } from '../scopes';

export interface SelectedFixture {
  elementId: string;
  target: TakeoffTarget;
  measure: 'count' | 'linear' | 'area';
  qty: number;
  length: number | null;
  /** Typed area (area fixtures). */
  area?: number | null;
  /** Measured run length in scene px (lines), for calibration. */
  pxLength: number | null;
  frameId: string | null;
  /** A line counting an EACH type along it (ceil(length / one fixture's length)). */
  along?: AlongRun | null;
  /** This placement's own SCOPE (blank = inherit zone / type / frame / BASE BID). */
  scope?: string;
  /** Its drawn zone's SCOPE, if any (for the "Inherit (…)" label). */
  zoneScope?: string;
}

export interface SelectedFrame {
  frameId: string;
  name: string;
}

export interface EstimatePanelProps {
  e: BoardEstimate;
  title: string;
  readOnly: boolean;
  /** Fixture tool armed with this target (null = disarmed). */
  armed: TakeoffTarget | null;
  onArm(target: TakeoffTarget | null): void;
  /** Place one fixture at the viewport centre (quick add with a quantity). */
  onPlaceAtCentre(target: TakeoffTarget, qty: number, length?: number | null): void | boolean;
  selection: SelectedFixture | null;
  frame: SelectedFrame | null;
  /** Selected plain line (not a fixture) — for frame-scale calibration. */
  selectedLinePx: { elementId: string; px: number; frameId: string | null; frameName: string } | null;
  onSetFixture(elementId: string, patch: { qty?: number | null; length?: number | null; area?: number | null }): void;
  /** Selected (or badge-clicked) drawing image: its scale and lock. */
  image?: SelectedImage | null;
  /** Measured length / area of the selected line, shape or run / area fixture. */
  measure?: SelectedMeasure | null;
  onCalibrateImage?(elementId: string): void;
  onPresetImage?(elementId: string, presetKey: string): void;
  onToggleImageLock?(elementId: string): void;
  onRemoveImageScale?(elementId: string): void;
  /** Tag a plain line / shape as a measurement (shows its length on the board). */
  onMeasureLine?(elementId: string): void;
  onReassign(elementId: string, target: TakeoffTarget): void;
  /** Count a plain drawn line as a run of a linear (LF / M) item. */
  onAssignLine(elementId: string, target: TakeoffTarget): void;
  onFocusTarget(target: TakeoffTarget): void;
  /** Drawn ZONES on the board with their fixture counts (boards only). Hidden zones still count. */
  zones?: { id: string; name: string; count: number; scope?: string; hidden?: boolean }[];
  onFocusZone?(id: string): void;
  onRenameZone?(id: string, name: string): void;
  /** SCOPE of a drawn zone (blank = inherit). */
  onZoneScope?(id: string, scope: string): void;
  /** Hide / show drawn zones (one undo step). Hidden zones still count for ZONE. */
  onZonesHidden?(ids: string[], hidden: boolean): void;
  /** SCOPE of one placed fixture (blank = inherit). */
  onFixtureScope?(elementId: string, scope: string): void;
  /** The SCOPE dropdown's list (the board computes it once for the toolbar cards too). */
  scopeChoices?: ScopeChoices;
  onDrawZone?(): void;
  onClose(): void;
  /** Focus the quick-add bar from the board's "/" key. */
  quickAddRef?: React.MutableRefObject<{ focus(): void } | null>;
}

type Tab = 'fixtures' | 'budget' | 'bom';

export function EstimatePanel(p: EstimatePanelProps) {
  const { e } = p;
  const [tab, setTab] = useState<Tab>('fixtures');
  const [editItem, setEditItem] = useState<TakeoffItemDTO | null | 'new'>(null);
  const [editHost, setEditHost] = useState<string | null | 'new'>(null);
  const b = e.budget;
  const wb = useWorkbookLink(e.workbook, e.onFrame);
  const linked = !!wb?.linked;
  const scopes = p.scopeChoices ?? estimateScopeChoices(e, wb, p.zones);
  return (
    <aside className="rl-est" data-testid="estimate-panel" aria-label="Design estimate" data-workbook={linked ? 'linked' : undefined}>
      <ToastHost />
      <header className="rl-est-head">
        <strong>Estimate</strong>
        <span className={`rl-est-live${e.live ? ' is-live' : ''}`} title={e.live ? 'Live' : 'Connecting…'} />
        <span className="rl-spacer" />
        {b && !linked ? (
          <span className="rl-est-total" data-testid="estimate-total" title="ROM total (range)">
            {money(b.totals.total, b.currency)}
          </span>
        ) : null}
        <EstimateExport e={e} title={p.title} />
        <WorkbookButton client={e.workbook} title={p.title} subscribe={e.onFrame} />
        <button className="rl-icon-btn" aria-label="Close estimate" onClick={p.onClose}>
          ×
        </button>
      </header>
      <nav className="rl-est-tabs" role="tablist">
        {(['fixtures', 'budget', 'bom'] as Tab[]).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'is-active' : ''} onClick={() => setTab(t)} data-testid={`estimate-tab-${t}`}>
            {t === 'fixtures' ? 'Fixtures' : t === 'budget' ? 'Budget' : 'BOM'}
          </button>
        ))}
      </nav>
      {e.error ? <p className="rl-est-warn">{e.error}</p> : null}
      {p.selection ? <SelectedFixtureCard p={p} scopes={scopes} /> : null}
      {!p.selection && p.frame ? <FrameCard p={p} /> : null}
      {!p.selection && !p.frame && p.image ? (
        <ImageScaleCard
          image={p.image}
          readOnly={p.readOnly}
          onCalibrate={() => p.onCalibrateImage?.(p.image!.elementId)}
          onPreset={(k) => p.onPresetImage?.(p.image!.elementId, k)}
          onToggleLock={() => p.onToggleImageLock?.(p.image!.elementId)}
          onRemoveScale={() => p.onRemoveImageScale?.(p.image!.elementId)}
        />
      ) : null}
      {!p.selection && !p.frame && !p.image && p.selectedLinePx ? <CalibrateCard p={p} /> : null}
      <div className="rl-est-body">
        {tab === 'fixtures' ? (
          <FixturesTab p={p} linked={linked} onEdit={(i) => setEditItem(i)} onEditHost={(id) => setEditHost(id)} />
        ) : tab === 'budget' ? (
          linked && wb ? <WorkbookPricing link={wb} /> : <BudgetTab e={e} readOnly={p.readOnly} />
        ) : (
          <TotalsPanel t={asTakeoffSession(e)} onFocusTarget={p.onFocusTarget} />
        )}
      </div>
      {editItem !== null ? <ItemEditor t={asTakeoffSession(e)} item={editItem === 'new' ? null : editItem} linked={linked} scopes={scopes} onClose={() => setEditItem(null)} /> : null}
      {editHost !== null ? (
        <AssemblyEditor t={asTakeoffSession(e)} assembly={editHost === 'new' ? null : e.assemblyById.get(editHost) ?? null} scopes={scopes} onClose={() => setEditHost(null)} />
      ) : null}
    </aside>
  );
}

// ---- fixtures tab ---------------------------------------------------------------------------

function FixturesTab({ p, onEdit, onEditHost, linked = false }: { p: EstimatePanelProps; onEdit(i: TakeoffItemDTO | 'new'): void; onEditHost(id: string | 'new'): void; linked?: boolean }) {
  const { e } = p;
  const [search, setSearch] = useState('');
  const totals = useMemo(() => new Map((e.quantities?.items ?? []).map((i) => [i.item_id, i.total])), [e.quantities]);
  const hostCounts = useMemo(() => new Map((e.quantities?.hosts ?? []).map((h) => [h.assembly_id, h.count])), [e.quantities]);
  const s = search.trim().toLowerCase();
  const items = e.items.filter((i) => !s || `${i.code} ${i.description} ${i.name} ${i.make} ${i.model}`.toLowerCase().includes(s));
  const presets = FIXTURE_PRESETS.filter((x) => !s || `${x.name} ${x.keywords.join(' ')}`.toLowerCase().includes(s));
  const addPreset = async (preset: FixturePreset) => {
    const row = await e.create<TakeoffItemDTO>('items', { ...itemFromPreset(preset, {}, e.state?.pricing?.measure_display), code: nextTypeCode(preset.prefix, e.items) });
    if (row) {
      toast(`${row.code} ${preset.name} added — click the board to place`, 'info');
      p.onArm({ kind: 'item', id: row.id });
    }
  };
  return (
    <div className="rl-est-fixtures">
      {!p.readOnly ? <QuickAdd p={p} /> : null}
      <input className="rl-input" placeholder="Search fixtures…" value={search} onChange={(ev) => setSearch(ev.target.value)} data-testid="estimate-search" />
      <Section title={`Project (${e.items.length + e.assemblies.length})`} extra={!p.readOnly ? <button className="rl-btn" data-size="sm" onClick={() => onEdit('new')}>+ Item</button> : null}>
        {e.assemblies.map((a, i) => (
          <Row
            key={a.id}
            active={p.armed?.id === a.id}
            swatch={<SymbolSwatch symbol={a.symbol} color={a.color} />}
            code={a.code || 'ASSY'}
            text={a.name}
            qty={hostCounts.get(a.id) ?? 0}
            uom="placed"
            slot={a.favorite_slot ?? (!e.items.some((x) => x.favorite_slot) && !e.assemblies.some((x) => x.favorite_slot) ? i + 1 : null)}
            onPick={() => p.onArm(p.armed?.id === a.id ? null : { kind: 'assembly', id: a.id })}
            onEdit={p.readOnly ? undefined : () => onEditHost(a.id)}
            onFind={(hostCounts.get(a.id) ?? 0) > 0 ? () => p.onFocusTarget({ kind: 'assembly', id: a.id }) : undefined}
            testId={`estimate-host-${a.name}`}
          />
        ))}
        {items.map((i, k) => (
          <Row
            key={i.id}
            active={p.armed?.id === i.id}
            swatch={<SymbolSwatch symbol={i.symbol} color={i.color} uom={i.uom} />}
            code={i.code}
            text={i.description || i.name}
            qty={totals.get(i.id) ?? 0}
            uom={i.uom}
            tbd={!linked && i.unit_cost === null}
            badge={workbookBadge(i, totals.get(i.id) ?? 0, linked)}
            slot={i.favorite_slot ?? (!e.items.some((x) => x.favorite_slot) && !e.assemblies.some((x) => x.favorite_slot) ? e.assemblies.length + k + 1 : null)}
            onPick={() => p.onArm(p.armed?.id === i.id ? null : { kind: 'item', id: i.id })}
            onEdit={p.readOnly ? undefined : () => onEdit(i)}
            onFind={(totals.get(i.id) ?? 0) > 0 ? () => p.onFocusTarget({ kind: 'item', id: i.id }) : undefined}
            testId={`estimate-item-${i.code}`}
          />
        ))}
        {!e.items.length && !e.assemblies.length ? <p className="rl-muted rl-est-hint">Add fixtures from the library below or type in the quick-add bar.</p> : null}
        {!p.readOnly ? (
          <button className="rl-btn" data-size="sm" onClick={() => onEditHost('new')} style={{ marginTop: 4 }}>
            + Assembly (pole, pylon, room package)
          </button>
        ) : null}
      </Section>
      {p.zones ? <ZonesSection p={p} scopes={p.scopeChoices ?? estimateScopeChoices(p.e, null, p.zones)} /> : null}
      {!p.readOnly
        ? PRESET_GROUPS.map((g) => {
            const list = presets.filter((x) => x.group === g);
            if (!list.length) return null;
            return (
              <Section key={`${g}:${s ? 'search' : ''}`} title={`Library · ${g}`} startOpen={g === 'Luminaires' || !!s}>
                <div className="rl-est-presets">
                  {list.map((x) => (
                    <button key={x.key} className="rl-est-preset" onClick={() => void addPreset(x)} title={`Add ${x.name} (${x.prefix}, ${x.uom}) to the project`} data-testid={`preset-${x.key}`}>
                      <SymbolSwatch symbol={x.symbol} color={x.color} uom={presetUom(x.uom, e.state?.pricing?.measure_display)} />
                      <span>{x.name}</span>
                      <span className="rl-muted">{x.uom === 'EACH' ? '' : presetUom(x.uom, e.state?.pricing?.measure_display)}</span>
                    </button>
                  ))}
                </div>
              </Section>
            );
          })
        : null}
    </div>
  );
}

/** Where a TYPE stands against the linked workbook, for the fixture list. */
function workbookBadge(i: TakeoffItemDTO, qty: number, linked: boolean): { text: string; kind: 'warn' | 'info'; title: string } | null {
  if (!linked) return null;
  const st = i.workbook_state;
  if (st === 'removed') return { text: 'removed from workbook', kind: 'warn', title: 'This TYPE is no longer in PROJECT BOM; its placements are kept but not counted.' };
  if (st === 'pending') return { text: 'adding to workbook…', kind: 'info', title: 'A TYPE-only row is being added to PROJECT BOM.' };
  if (!qty) return { text: 'not placed · Place', kind: 'info', title: 'In the workbook but not on the board yet — click to place it.' };
  return null;
}

function Row(r: {
  active: boolean;
  swatch: ReactNode;
  code: string;
  text: string;
  qty: number;
  uom: string;
  tbd?: boolean;
  badge?: { text: string; kind: 'warn' | 'info'; title: string } | null;
  slot: number | null;
  onPick(): void;
  onEdit?: () => void;
  /** Select + zoom to every placement of this type on the board. */
  onFind?: () => void;
  testId?: string;
}) {
  return (
    <div className={`rl-est-row${r.active ? ' is-active' : ''}`} data-testid={r.testId}>
      <button className="rl-est-pick" onClick={r.onPick} title="Arm the fixture tool (I), then click the board">
        {r.swatch}
        <span className="rl-est-code">{r.code}</span>
        <span className="rl-est-text">{r.text}</span>
        {r.tbd ? <span className="rl-est-tbd">TBD</span> : null}
        {r.badge ? (
          <span className="rl-est-wb" data-kind={r.badge.kind} title={r.badge.title} data-testid={r.testId ? `${r.testId}-wb` : undefined}>
            {r.badge.text}
          </span>
        ) : null}
        <span className="rl-est-qty" data-testid={r.testId ? `${r.testId}-qty` : undefined}>
          {fmt(r.qty)} <span className="rl-muted">{r.uom === 'EACH' ? '' : r.uom}</span>
        </span>
        {r.slot && r.slot <= 9 ? <kbd className="rl-est-kbd">{r.slot}</kbd> : null}
      </button>
      {r.onFind ? (
        <button className="rl-icon-btn" aria-label={`Show all ${r.code} on the board`} title={`Select and zoom to every ${r.code} on the board`} onClick={r.onFind} data-testid={r.testId ? `${r.testId}-find` : undefined}>
          ⌖
        </button>
      ) : null}
      {r.onEdit ? (
        <button className="rl-icon-btn" aria-label={`Edit ${r.code}`} onClick={r.onEdit}>
          ✎
        </button>
      ) : null}
    </div>
  );
}

function QuickAdd({ p }: { p: EstimatePanelProps }) {
  const { e } = p;
  const [text, setText] = useState('');
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (p.quickAddRef) p.quickAddRef.current = { focus: () => ref.current?.focus() };
  }, [p.quickAddRef]);
  const parsed = useMemo(() => (text.trim() ? parseQuickAdd(text) : null), [text]);
  const submit = async () => {
    const q = parsed;
    if (!q) return;
    let item = q.code ? e.items.find((i) => i.code === q.code) ?? null : null;
    if (!item) {
      const prefix = q.prefix ?? 'FX';
      const data: Record<string, unknown> = {
        ...(q.preset ? itemFromPreset(q.preset, {}, e.state?.pricing?.measure_display) : { source: 'design' }),
        code: q.code ?? nextTypeCode(prefix, e.items),
        description: q.description || q.preset?.description || 'FIXTURE',
        ...(q.wattage ? { wattage: q.wattage } : {}),
        ...(q.unitCost !== null ? { unit_cost: q.unitCost.toFixed(2), price_source: 'guess' } : {}),
        ...(q.labourHours !== null ? { labour_hours: q.labourHours } : {}),
        ...(q.length !== null && !q.preset ? { uom: 'LF' } : {}),
      };
      item = await e.create<TakeoffItemDTO>('items', data);
      if (!item) return;
    }
    const target: TakeoffTarget = { kind: 'item', id: item.id };
    if (q.qty !== null || q.length !== null) {
      // false: the board can't place right now (a zone is being drawn) — keep the text, arm nothing.
      if (p.onPlaceAtCentre(target, q.qty ?? 1, q.length) === false) return;
      toast(`${item.code} placed${q.qty ? ` ×${q.qty}` : ''} — fixture tool stays armed`, 'info');
    } else toast(`${item.code} ready — click the board to place`, 'info');
    p.onArm(target);
    setText('');
  };
  return (
    <div className="rl-est-quick">
      <input
        ref={ref}
        className="rl-input"
        placeholder='Quick add: "LD 6 downlight 12W $180"'
        value={text}
        onChange={(ev) => setText(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') void submit();
          // ⌘/Ctrl+Z (⇧ = redo) in the empty bar — e.g. right after placing — undoes on the board, not in the input.
          if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'z' && !text) {
            ev.preventDefault();
            const host = document.querySelector('.excalidraw') ?? document.body;
            dispatchNativeKey(host, { key: ev.key, code: 'KeyZ', metaKey: ev.metaKey, ctrlKey: ev.ctrlKey, shiftKey: ev.shiftKey });
          }
          if (ev.key === 'Escape') {
            setText('');
            ref.current?.blur();
          }
        }}
        aria-label="Quick add fixture"
        data-testid="estimate-quick-add"
      />
      {parsed ? (
        <p className="rl-muted rl-est-parse" data-testid="estimate-quick-parse">
          {parsed.code ?? (parsed.prefix ? `${parsed.prefix}·· (new)` : 'FX·· (new)')} · {parsed.description || '—'}
          {parsed.qty !== null ? ` · ×${parsed.qty}` : ''}
          {parsed.length !== null ? ` · ${parsed.length} ft` : ''}
          {parsed.wattage ? ` · ${parsed.wattage}` : ''}
          {parsed.unitCost !== null ? ` · ${money(parsed.unitCost, e.budget?.currency)}` : ''}
          {parsed.labourHours !== null ? ` · ${parsed.labourHours} h` : ''}
        </p>
      ) : null}
    </div>
  );
}

// ---- selection cards ----------------------------------------------------------------------------

/** The SCOPE list for an estimate: the workbook's register (when linked) + every scope the board already uses. */
export function estimateScopeChoices(
  e: Pick<BoardEstimate, 'items' | 'assemblies' | 'state'>,
  link: Parameters<typeof scopeChoices>[0],
  zones?: { scope?: string }[],
  placements: readonly (string | undefined)[] = [],
): ScopeChoices {
  const inUse = [
    ...e.items.map((i) => i.scope),
    ...e.assemblies.map((a) => a.scope),
    ...(e.state?.areas ?? []).map((a) => a.scope),
    ...(zones ?? []).map((z) => z.scope),
    ...placements,
  ].filter((v): v is string => !!v && !!v.trim());
  return scopeChoices(link, inUse);
}

/** "Inherit (…)" label for a placement: what its zone / type would give it. */
function inheritedScope(e: BoardEstimate, s: SelectedFixture): string {
  const typeScope = s.target.kind === 'item' ? e.itemById.get(s.target.id)?.scope : e.assemblyById.get(s.target.id)?.scope;
  if (s.zoneScope) return `zone: ${s.zoneScope}`;
  if (typeScope) return `type: ${typeScope}`;
  return 'zone / type / frame / BASE BID';
}

function SelectedFixtureCard({ p, scopes }: { p: EstimatePanelProps; scopes: ScopeChoices }) {
  const { e } = p;
  const s = p.selection!;
  const info = e.targetInfo(s.target);
  const seq = e.sequence.get(s.elementId);
  const [qty, setQty] = useState(String(s.qty));
  const typed = s.measure === 'area' ? s.area ?? null : s.length;
  const [len, setLen] = useState(typed !== null ? String(typed) : '');
  useEffect(() => {
    setQty(String(s.qty));
    setLen(typed !== null ? String(typed) : '');
  }, [s.elementId, s.qty, typed]);
  const m = p.measure && p.measure.elementId === s.elementId ? p.measure : null;
  return (
    <div className="rl-est-card" data-testid="estimate-selected">
      <div className="rl-est-card-h">
        {info ? <SymbolSwatch symbol={info.symbol} color={info.color} /> : null}
        <strong>
          {info?.short ?? '?'}
          {seq ? `-${seq}` : ''}
        </strong>
        <span className="rl-muted">{info?.label}</span>
      </div>
      {s.along ? (
        <p className="rl-est-hint" data-testid="estimate-along">
          Counted along the run: as many as fit, one fixture = {s.along.unit_length} {s.along.unit} (rounded up). Change it from the line's card on
          the toolbar.
        </p>
      ) : s.measure === 'count' ? (
        <label className="rl-est-field">
          <span>Qty on this symbol</span>
          <input
            className="rl-input"
            type="number"
            min={1}
            step={1}
            value={qty}
            disabled={p.readOnly}
            onChange={(ev) => setQty(ev.target.value)}
            onBlur={() => p.onSetFixture(s.elementId, { qty: Number(qty) > 0 ? Number(qty) : 1 })}
            onKeyDown={(ev) => ev.key === 'Enter' && (ev.target as HTMLInputElement).blur()}
            data-testid="estimate-qty"
          />
        </label>
      ) : (
        <label className="rl-est-field">
          <span>
            {s.measure === 'area' ? 'Area' : 'Length'} ({info?.uom ?? (s.measure === 'area' ? 'SF' : 'LF')}) — blank = measure on the drawing
          </span>
          <input
            className="rl-input"
            type="number"
            min={0}
            value={len}
            disabled={p.readOnly}
            placeholder={m?.text ?? (s.pxLength ? `${Math.round(s.pxLength)} px drawn` : '')}
            onChange={(ev) => setLen(ev.target.value)}
            onBlur={() => p.onSetFixture(s.elementId, s.measure === 'area' ? { area: len === '' ? null : Number(len) } : { length: len === '' ? null : Number(len) })}
            onKeyDown={(ev) => ev.key === 'Enter' && (ev.target as HTMLInputElement).blur()}
            data-testid="estimate-length"
          />
        </label>
      )}
      {(s.measure !== 'count' || s.along) && m && typed === null ? <MeasureLine m={m} readOnly onMeasure={() => undefined} /> : null}
      {!p.readOnly && e.items.length + e.assemblies.length > 1 ? (
        <label className="rl-est-field">
          <span>Fixture type</span>
          <select
            className="rl-select"
            value={s.target.id}
            onChange={(ev) => {
              const id = ev.target.value;
              p.onReassign(s.elementId, e.itemById.has(id) ? { kind: 'item', id } : { kind: 'assembly', id });
            }}
          >
            {e.assemblies.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code || 'ASSY'} {a.name}
              </option>
            ))}
            {e.items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.code} {i.description || i.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {p.onFixtureScope ? (
        <label className="rl-est-field">
          <span>Scope (this fixture only)</span>
          <ScopeSelect
            value={s.scope ?? ''}
            onChange={(v) => p.onFixtureScope!(s.elementId, v)}
            choices={scopes}
            inheritLabel={inheritedScope(e, s)}
            disabled={p.readOnly}
            testId="estimate-fixture-scope"
          />
        </label>
      ) : null}
    </div>
  );
}

function FrameCard({ p }: { p: EstimatePanelProps }) {
  const { e } = p;
  const f = p.frame!;
  const area = e.quantities?.board_areas?.find((a) => a.frame_id === f.frameId);
  const row = e.state?.areas.find((a) => a.frame_id === f.frameId);
  const [mult, setMult] = useState(String(row?.multiplier ?? 1));
  const [scope, setScope] = useState(row?.scope ?? '');
  useEffect(() => {
    setMult(String(row?.multiplier ?? 1));
    setScope(row?.scope ?? '');
  }, [f.frameId, row?.multiplier, row?.scope]);
  const budgetArea = e.budget?.areas.find((a) => a.area === (area?.name ?? f.name));
  return (
    <div className="rl-est-card" data-testid="estimate-frame">
      <div className="rl-est-card-h">
        <strong>{area?.name ?? f.name}</strong>
        <span className="rl-muted">area</span>
        <span className="rl-spacer" />
        {budgetArea ? <span>{money(budgetArea.total, e.budget?.currency)}</span> : null}
      </div>
      <div className="rl-est-grid2">
        <label className="rl-est-field">
          <span>Typical unit ×</span>
          <input
            className="rl-input"
            type="number"
            min={1}
            value={mult}
            disabled={p.readOnly}
            onChange={(ev) => setMult(ev.target.value)}
            onBlur={() => Number(mult) > 0 && Number(mult) !== (row?.multiplier ?? 1) && void e.setFrame(f.frameId, area?.name ?? f.name, { multiplier: Number(mult) })}
            onKeyDown={(ev) => ev.key === 'Enter' && (ev.target as HTMLInputElement).blur()}
            data-testid="estimate-frame-multiplier"
          />
        </label>
        <label className="rl-est-field">
          <span>Scope</span>
          <input
            className="rl-input"
            value={scope}
            placeholder={e.state?.default_scope ?? 'BASE BID'}
            disabled={p.readOnly}
            onChange={(ev) => setScope(ev.target.value.toUpperCase())}
            onBlur={() => scope !== (row?.scope ?? '') && void e.setFrame(f.frameId, area?.name ?? f.name, { scope })}
            onKeyDown={(ev) => ev.key === 'Enter' && (ev.target as HTMLInputElement).blur()}
          />
        </label>
      </div>
      <p className="rl-muted rl-est-hint">
        Scale: {row?.units_per_px ? `1 px = ${fmt(row.units_per_px)} ${row.scale_unit ?? 'ft'}` : 'not set — select a line over a known dimension to calibrate'}
      </p>
    </div>
  );
}

function CalibrateCard({ p }: { p: EstimatePanelProps }) {
  const { e } = p;
  const l = p.selectedLinePx!;
  const [real, setReal] = useState('');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const runItems = e.items.filter((i) => i.uom === 'LF' || i.uom === 'M');
  const [run, setRun] = useState('');
  const assign = runItems.length && !p.readOnly ? (
    <div className="rl-est-grid2">
      <select className="rl-select" value={run} onChange={(ev) => setRun(ev.target.value)} aria-label="Run of" data-testid="estimate-run-item">
        <option value="">Count as a run of…</option>
        {runItems.map((i) => (
          <option key={i.id} value={i.id}>
            {i.code} {i.description || i.name}
          </option>
        ))}
      </select>
      <button className="rl-btn" disabled={!run} onClick={() => p.onAssignLine(l.elementId, { kind: 'item', id: run })} data-testid="estimate-run-assign">
        Assign
      </button>
    </div>
  ) : null;
  const m = p.measure && p.measure.elementId === l.elementId ? p.measure : null;
  const measured = m ? <MeasureLine m={m} readOnly={p.readOnly} onMeasure={() => p.onMeasureLine?.(l.elementId)} /> : null;
  if (!l.frameId || m?.text)
    return (
      <div className="rl-est-card" data-testid="estimate-line">
        {measured}
        {!m?.text ? <p className="rl-muted rl-est-hint">Draw the line inside a frame to use it as that frame's scale.</p> : null}
        {assign}
      </div>
    );
  return (
    <div className="rl-est-card" data-testid="estimate-calibrate">
      <div className="rl-est-card-h">
        <strong>Scale for {l.frameName}</strong>
        <span className="rl-muted">{Math.round(l.px)} px drawn</span>
      </div>
      {measured}
      <div className="rl-est-grid2">
        <input className="rl-input" type="number" min={0} placeholder="Real length" value={real} onChange={(ev) => setReal(ev.target.value)} data-testid="estimate-calibrate-length" />
        <select className="rl-select" value={unit} onChange={(ev) => setUnit(ev.target.value as 'ft' | 'm')}>
          <option value="ft">ft</option>
          <option value="m">m</option>
        </select>
      </div>
      <button
        className="rl-btn"
        data-variant="primary"
        disabled={p.readOnly || !(Number(real) > 0) || l.px <= 0}
        onClick={() => void e.setFrame(l.frameId!, l.frameName, { units_per_px: Number(real) / l.px, scale_unit: unit }).then((r) => r && toast(`Scale set for ${l.frameName}`, 'info'))}
        data-testid="estimate-calibrate-save"
      >
        Use as scale
      </button>
      {assign}
    </div>
  );
}

// ---- budget tab -----------------------------------------------------------------------------------

const SETTING_FIELDS: { key: keyof PricingSettings; label: string; suffix: string }[] = [
  { key: 'labour_rate', label: 'Labour rate', suffix: '/h' },
  { key: 'labour_pct', label: 'Labour (default)', suffix: '% of material' },
  { key: 'tax_pct', label: 'Tax', suffix: '% of material' },
  { key: 'freight_pct', label: 'Freight', suffix: '% of material' },
  { key: 'contingency_pct', label: 'Contingency', suffix: '% of subtotal' },
  { key: 'markup_pct', label: 'Markup', suffix: '% of subtotal + contingency' },
  { key: 'rom_low_pct', label: 'ROM low', suffix: '% below' },
  { key: 'rom_high_pct', label: 'ROM high', suffix: '% above' },
];

function BudgetTab({ e, readOnly }: { e: BoardEstimate; readOnly: boolean }) {
  const b = e.budget;
  const s = e.state?.pricing;
  const [showSettings, setShowSettings] = useState(false);
  if (!b) return <div className="rl-center">{e.error ? <p className="rl-error">{e.error}</p> : <span className="rl-spinner" />}</div>;
  const t = b.totals;
  const cur = b.currency;
  const line = (label: string, v: number, note?: string, strong = false, testId?: string) => (
    <tr className={strong ? 'is-strong' : ''}>
      <td>{label}</td>
      <td className="rl-muted">{note}</td>
      <td className="num" data-testid={testId}>
        {money(v, cur)}
      </td>
    </tr>
  );
  return (
    <div className="rl-est-budget" data-testid="estimate-budget">
      <div className="rl-est-rom">
        <span className="rl-muted">ROM budget</span>
        <strong data-testid="estimate-rom-range">{moneyRange(t.low, t.high, cur)}</strong>
        <span className="rl-muted">total {money(t.total, cur)}</span>
      </div>
      {b.tbd.unpriced.length || b.tbd.unspecified.length ? (
        <p className="rl-est-warn" data-testid="estimate-tbd">
          {b.tbd.unpriced.length ? `TBD price: ${b.tbd.unpriced.join(', ')}. ` : ''}
          {b.tbd.unspecified.length ? `No make/model yet: ${b.tbd.unspecified.length} type${b.tbd.unspecified.length === 1 ? '' : 's'}.` : ''}
        </p>
      ) : null}
      <table className="rl-to-table">
        <tbody>
          {line('Material', t.material, undefined, false, 'budget-material')}
          {line('Labour', t.labour, t.labour_hours ? `${fmt(t.labour_hours)} h` : undefined, false, 'budget-labour')}
          {t.tax ? line('Tax', t.tax) : null}
          {t.freight ? line('Freight', t.freight) : null}
          {line('Subtotal', t.subtotal, undefined, true)}
          {line('Contingency', t.contingency, `${fmt(b.settings.contingency_pct)}%`)}
          {t.markup ? line('Markup', t.markup, `${fmt(b.settings.markup_pct)}%`) : null}
          {line('Total', t.total, cur, true, 'budget-total')}
        </tbody>
      </table>
      {b.confidence.length ? (
        <div className="rl-est-conf" title="Share of priced material by confidence">
          {b.confidence.map((c) => (
            <span key={c.confidence} data-conf={c.confidence} style={{ flexGrow: c.share || 0.01 }}>
              {c.confidence} {Math.round(c.share * 100)}%
            </span>
          ))}
        </div>
      ) : null}
      {b.areas.length ? (
        <Section title="By area (frame)">
          <table className="rl-to-table">
            <tbody>
              {b.areas.map((a) => (
                <tr key={a.area}>
                  <td>{a.area}</td>
                  <td className="num">{money(a.total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
      {b.categories.length ? (
        <Section title="By category" startOpen={false}>
          <table className="rl-to-table">
            <tbody>
              {b.categories.map((c) => (
                <tr key={c.category}>
                  <td>{c.category}</td>
                  <td className="num">{money(c.total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
      <Section title="Lines" startOpen={false}>
        <table className="rl-to-table">
          <thead>
            <tr>
              <th>TYPE</th>
              <th className="num">Qty</th>
              <th className="num">Unit</th>
              <th className="num">Material</th>
            </tr>
          </thead>
          <tbody>
            {b.lines
              .filter((l) => l.qty)
              .map((l) => (
                <tr key={l.item_id}>
                  <td title={l.description}>{l.type}</td>
                  <td className="num">{fmt(l.qty)}</td>
                  <td className="num">{l.unit_cost === null ? 'TBD' : money(l.unit_cost, cur)}</td>
                  <td className="num">{l.material === null ? 'TBD' : money(l.material, cur)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </Section>
      <button className="rl-btn" data-size="sm" onClick={() => setShowSettings((v) => !v)} data-testid="estimate-settings-toggle">
        {showSettings ? 'Hide' : 'Budget settings'}
      </button>
      {showSettings && s ? <SettingsForm e={e} s={s} readOnly={readOnly} /> : null}
      <p className="rl-muted rl-est-hint">Order: {b.order_of_operations.join(' → ')}.</p>
    </div>
  );
}

function SettingsForm({ e, s, readOnly }: { e: BoardEstimate; s: PricingSettings; readOnly: boolean }) {
  return (
    <div className="rl-est-settings" data-testid="estimate-settings">
      <label className="rl-est-field">
        <span>Currency</span>
        <input className="rl-input" defaultValue={s.currency} maxLength={3} disabled={readOnly} onBlur={(ev) => ev.target.value.toUpperCase() !== s.currency && void e.setPricing({ currency: ev.target.value.toUpperCase() })} />
      </label>
      <label className="rl-est-field">
        <span>Measurements</span>
        <select
          className="rl-select"
          value={s.measure_display ?? 'ftin'}
          disabled={readOnly}
          onChange={(ev) => void e.setPricing({ measure_display: ev.target.value as NonNullable<PricingSettings['measure_display']> })}
          data-testid="estimate-setting-measure_display"
        >
          {MEASURE_DISPLAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {SETTING_FIELDS.map((f) => (
        <label key={f.key} className="rl-est-field">
          <span>
            {f.label} <span className="rl-muted">{f.suffix}</span>
          </span>
          <input
            className="rl-input"
            type="number"
            min={0}
            defaultValue={String(s[f.key])}
            key={`${f.key}:${s[f.key]}`}
            disabled={readOnly}
            onBlur={(ev) => Number(ev.target.value) !== s[f.key] && ev.target.value !== '' && void e.setPricing({ [f.key]: Number(ev.target.value) })}
            onKeyDown={(ev) => ev.key === 'Enter' && (ev.target as HTMLInputElement).blur()}
            data-testid={`estimate-setting-${f.key}`}
          />
        </label>
      ))}
    </div>
  );
}

// ---- export ------------------------------------------------------------------------------------------

function EstimateExport({ e, title }: { e: BoardEstimate; title: string }) {
  const [busy, setBusy] = useState(false);
  const safe = (title || 'Estimate').replace(/[^\w .-]+/g, '').trim() || 'Estimate';
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <select
      className="rl-select"
      data-size="sm"
      style={{ width: 'auto' }}
      value=""
      disabled={busy}
      aria-label="Export estimate"
      data-testid="estimate-export"
      onChange={(ev) => {
        const v = ev.target.value;
        ev.target.value = '';
        if (v === 'xlsx') void run(async () => download(await e.client.exportXlsx(), `${safe} - Estimate.xlsx`));
        else if (v.startsWith('csv:')) {
          const table = v.slice(4) as 'tuples' | 'project_bom' | 'area_zone_bom' | 'budget';
          void run(async () => download(await e.client.exportCsv(table), `${safe} - Estimate ${table}.csv`));
        } else if (v === 'sheets')
          void run(async () => {
            const r = await e.client.exportSheets();
            toast(`Google Sheet created: ${r.name}`, 'info', 6000);
            window.open(r.url, '_blank', 'noopener');
          });
      }}
    >
      <option value="">{busy ? 'Exporting…' : 'Export'}</option>
      <option value="xlsx">Excel (BOM + ROM budget)</option>
      <option value="sheets">Google Sheets (new project sheet)</option>
      <option value="csv:budget">CSV — ROM budget</option>
      <option value="csv:project_bom">CSV — PROJECT BOM</option>
      <option value="csv:area_zone_bom">CSV — AREA/ZONE BOM</option>
    </select>
  );
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- bits -----------------------------------------------------------------------------------------------

function ZonesSection({ p, scopes }: { p: EstimatePanelProps; scopes: ScopeChoices }) {
  const zones = p.zones ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const anyShown = zones.some((z) => !z.hidden);
  const canHide = !p.readOnly && !!p.onZonesHidden && zones.length > 0;
  return (
    <Section
      title={`Zones (${zones.length})`}
      extra={
        <>
          {canHide ? (
            <button
              className="rl-btn"
              data-size="sm"
              title={anyShown ? 'Hide every zone on the board (they still count for ZONE)' : 'Show every zone again'}
              onClick={() => p.onZonesHidden!(zones.filter((z) => !!z.hidden !== anyShown).map((z) => z.id), anyShown)}
              data-testid={anyShown ? 'estimate-zones-hide-all' : 'estimate-zones-show-all'}
            >
              {anyShown ? 'Hide all' : 'Show all'}
            </button>
          ) : null}
          {!p.readOnly && p.onDrawZone ? <button className="rl-btn" data-size="sm" onClick={p.onDrawZone} data-testid="estimate-zone-draw">+ Zone</button> : null}
        </>
      }
    >
      {!zones.length ? <p className="rl-muted rl-est-hint">Draw a zone (Z) around fixtures: they go in that ZONE on the AREA/ZONE BOM.</p> : null}
      {zones.map((z) => (
        <div key={z.id} className={`rl-est-row rl-est-zone${z.hidden ? ' is-hidden' : ''}`} data-hidden={z.hidden || undefined} data-testid={`estimate-zone-${z.name}`}>
          {canHide ? (
            <button
              className="rl-icon-btn rl-zone-eye"
              aria-pressed={!z.hidden}
              aria-label={z.hidden ? `Show ${z.name}` : `Hide ${z.name}`}
              title={z.hidden ? `Show ${z.name} on the board` : `Hide ${z.name} (set and forget — it still counts for ZONE)`}
              onClick={() => p.onZonesHidden!([z.id], !z.hidden)}
              data-testid={`estimate-zone-${z.name}-hide`}
            >
              {z.hidden ? '◌' : '◉'}
            </button>
          ) : null}
          {editing === z.id ? (
            <input
              className="rl-input"
              data-size="sm"
              value={name}
              autoFocus
              onChange={(ev) => setName(ev.target.value.toUpperCase())}
              onBlur={() => {
                if (name.trim() && name.trim() !== z.name) p.onRenameZone?.(z.id, name.trim());
                setEditing(null);
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur();
                if (ev.key === 'Escape') setEditing(null);
              }}
              aria-label={`Rename ${z.name}`}
              data-testid={`estimate-zone-${z.name}-input`}
            />
          ) : (
            <span className="rl-est-pick" style={{ cursor: 'default' }}>
              <span className="rl-est-code">{z.name}</span>
              <span className="rl-est-qty" data-testid={`estimate-zone-${z.name}-count`}>
                {z.count} <span className="rl-muted">fixture{z.count === 1 ? '' : 's'}</span>
              </span>
            </span>
          )}
          {z.count && p.onFocusZone ? (
            <button className="rl-icon-btn" aria-label={`Show the fixtures in ${z.name}`} title={`Select and zoom to the fixtures in ${z.name}`} onClick={() => p.onFocusZone!(z.id)} data-testid={`estimate-zone-${z.name}-find`}>
              ⌖
            </button>
          ) : null}
          {p.onZoneScope ? (
            <ScopeSelect
              size="sm"
              value={z.scope ?? ''}
              onChange={(v) => p.onZoneScope!(z.id, v)}
              choices={scopes}
              inheritLabel="type / frame"
              disabled={p.readOnly}
              testId={`estimate-zone-${z.name}-scope`}
            />
          ) : null}
          {!p.readOnly && p.onRenameZone ? (
            <button
              className="rl-icon-btn"
              aria-label={`Rename ${z.name}`}
              onClick={() => {
                setName(z.name);
                setEditing(z.id);
              }}
            >
              ✎
            </button>
          ) : null}
        </div>
      ))}
    </Section>
  );
}

function Section({ title, children, extra, startOpen = true }: { title: string; children: ReactNode; extra?: ReactNode; startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <section className="rl-est-section">
      <div className="rl-est-section-h">
        <button className="rl-section-h" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? '▾' : '▸'} {title}
        </button>
        <span className="rl-spacer" />
        {extra}
      </div>
      {open ? children : null}
    </section>
  );
}

function fmt(x: number | null | undefined): string {
  if (x === null || x === undefined) return '';
  return Number.isInteger(x) ? String(x) : String(Math.round(x * 100) / 100);
}
